import React, { createContext, useContext, useEffect, useState } from 'react'

import { LOCAL_STORAGE_KEYS } from '@/lib/storage-keys'

import type { CreateCheckoutSessionRequestMarketingConsent } from '@/clients/payment-service'

export type MarketingConsent = CreateCheckoutSessionRequestMarketingConsent
export interface LeadData {
  marketingConsent: MarketingConsent | null
  email: string | null
}

interface LeadContextProps {
  leadData: LeadData
  setLeadData: React.Dispatch<React.SetStateAction<LeadData>>
  isLeadInitialised: boolean
}

export const LeadContext = createContext<LeadContextProps | undefined>(undefined)

interface LeadProviderProps {
  children: React.ReactNode
}

export const LeadProvider: React.FC<LeadProviderProps> = ({ children }) => {
  const [leadData, setLeadData] = useState<LeadData>({
    marketingConsent: null,
    email: null,
  })
  const [isLeadInitialised, setIsLeadInitialised] = useState(false)

  useEffect(() => {
    setIsLeadInitialised(false)
    if (typeof window !== 'undefined') {
      try {
        const storedLead = localStorage.getItem(LOCAL_STORAGE_KEYS.LEAD)
        if (storedLead) {
          setLeadData(JSON.parse(storedLead))
        }
      } catch (error) {
        console.error('Error loading lead data from localStorage:', error)
      }
    }
    setIsLeadInitialised(true)
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && isLeadInitialised) {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEYS.LEAD, JSON.stringify(leadData))
      } catch (error) {
        console.error('Error saving lead data to localStorage:', error)
      }
    }
  }, [leadData, isLeadInitialised])

  return <LeadContext.Provider value={{ leadData, setLeadData, isLeadInitialised }}>{children}</LeadContext.Provider>
}

export const useLead = () => {
  const context = useContext(LeadContext)
  if (context === undefined) {
    throw new Error('useLead must be used within a LeadProvider')
  }
  return context
}
